import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { List, Icon, Button, Input, Dimmer, Loader, Menu, Modal, Form } from 'semantic-ui-react';

import GET_PROJECT_CATEGORIES from '../../../../graphql/queries/getProjectCategories';
import GET_PROJECT from '../../../../graphql/queries/getProject';
import CREATE_PROJECT_CATEGORY from '../../../../graphql/mutations/createProjectCategory';
import UPDATE_PROJECT_CATEGORY from '../../../../graphql/mutations/updateProjectCategory';
import DELETE_PROJECT_CATEGORY from '../../../../graphql/mutations/deleteProjectCategory';
import MOVE_PROJECT_CATEGORY from '../../../../graphql/mutations/moveProjectCategory';
import MOVE_WORK_IN_PROJECT_CATEGORY from '../../../../graphql/mutations/moveWorkInProjectCategory';

const sortItems = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);

// Helper to build the tree from flat array
const buildTree = (categories, projectWorks, allWorks) => {
  const map = {};
  const roots = [];
  const categorizedWorkIds = new Set();

  // Initialize map with categories
  categories.forEach(cat => {
    map[cat.id] = { ...cat, children: [], works: [] };
  });

  // Assign categories to their parents or roots
  categories.forEach(cat => {
    if (cat.parentId && map[cat.parentId]) {
      map[cat.parentId].children.push(map[cat.id]);
    } else {
      roots.push(map[cat.id]);
    }
  });

  // Sort categories
  Object.values(map).forEach(node => {
    node.children.sort(sortItems);
  });
  roots.sort(sortItems);

  const rootWorks = [];

  // Group works that belong to categories
  projectWorks.forEach(work => {
    if (work.categoryId && map[work.categoryId]) {
      map[work.categoryId].works.push(work);
      categorizedWorkIds.add(work.id);
    } else {
      rootWorks.push(work);
      categorizedWorkIds.add(work.id);
    }
  });

  // Sort categorized works
  Object.values(map).forEach(node => {
    node.works.sort(sortItems);
  });
  rootWorks.sort(sortItems);

  // All other works go to unassigned
  const unassignedWorks = [];
  allWorks.forEach(work => {
    if (!categorizedWorkIds.has(work.id)) {
      unassignedWorks.push(work);
    }
  });

  unassignedWorks.sort((a, b) => a.name.localeCompare(b.name));

  return { roots, rootWorks, unassignedWorks };
};

// Render a Single Work
const WorkItem = ({ work, index, onDelete, idPrefix = 'work-' }) => (
  <Draggable draggableId={`${idPrefix}${work.id}`} index={index}>
    {(provided, snapshot) => {
      const child = (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            userSelect: 'none',
            padding: '8px',
            margin: '0 0 4px 0',
            backgroundColor: snapshot.isDragging ? '#e6f7ff' : '#f9f9f9',
            border: '1px solid #ddd',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...provided.draggableProps.style,
          }}
        >
          <div {...provided.dragHandleProps} style={{ cursor: 'grab', flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Icon name="tasks" color="grey" style={{ marginRight: '8px' }} /> {work.name}
          </div>
          {onDelete && (
            <Button type="button" size="mini" basic icon="trash" color="red" onClick={(e) => { e.stopPropagation(); onDelete(work.id); }} />
          )}
        </div>
      );
      
      return child;
    }}
  </Draggable>
);

// Render a Category Node (Recursive)
const CategoryNode = ({ category, level, index, onAdd, onEdit, onDelete, onDeleteWork }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(category.name);

  const handleSave = () => {
    onEdit(category.id, editName);
    setIsEditing(false);
  };

  return (
    <Draggable draggableId={`cat-${category.id}`} index={index}>
      {(provided, snapshot) => {
        const child = (
          <div
            id={`cat-node-${category.id}`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
              userSelect: 'none',
              padding: '8px',
              margin: '0 0 8px 0',
              backgroundColor: snapshot.isDragging ? '#f0f0f0' : '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              ...provided.draggableProps.style,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div {...provided.dragHandleProps} style={{ cursor: 'grab', flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <Icon name="folder open" color="teal" />
                {isEditing ? (
                  <Input
                    size="small"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSave();
                      }
                    }}
                    action={<Button type="button" icon="check" color="teal" onClick={handleSave} />}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <strong style={{ marginLeft: '8px' }}>{category.name}</strong>
                )}
              </div>
              
              <div>
                {level < 2 && (
                  <Button type="button" size="mini" basic icon="plus" title="Add Subcategory" onClick={() => onAdd(category.id)} />
                )}
                <Button type="button" size="mini" basic icon="pencil" onClick={() => setIsEditing(!isEditing)} />
                <Button type="button" size="mini" basic icon="trash" color="red" onClick={() => onDelete(category.id)} />
              </div>
            </div>

            <div style={{ paddingLeft: '20px', borderLeft: '2px solid #eee', marginLeft: '10px' }}>
              {/* Works inside this category */}
              <Droppable droppableId={`works-${category.id}`} type="WORK">
                {(provided, dropSnapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      padding: '4px',
                      minHeight: '40px',
                      backgroundColor: dropSnapshot.isDraggingOver ? '#f0f9ff' : 'transparent',
                    }}
                  >
                    {category.works.map((work, workIndex) => (
                      <WorkItem key={`work-${work.id}`} work={work} index={workIndex} onDelete={onDeleteWork} />
                    ))}
                    {provided.placeholder}
                    {category.works.length === 0 && <div style={{ color: '#aaa', fontSize: '0.85em', fontStyle: 'italic' }}>Drop works here</div>}
                  </div>
                )}
              </Droppable>

              {/* Subcategories inside this category */}
              {level < 2 && (
                <Droppable droppableId={`cats-${category.id}`} type="CATEGORY">
                  {(provided, dropSnapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        marginTop: '8px',
                        padding: '4px',
                        minHeight: '20px',
                        backgroundColor: dropSnapshot.isDraggingOver ? '#f9f0ff' : 'transparent',
                      }}
                    >
                      {category.children.map((childCat, childIndex) => (
                        <CategoryNode 
                          key={`cat-${childCat.id}`} 
                          category={childCat} 
                          level={level + 1} 
                          index={childIndex}
                          onAdd={onAdd}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onDeleteWork={onDeleteWork}
                        />
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          </div>
        );
        
        return child;
      }}
    </Draggable>
  );
};

export const CategoriesManager = ({ projectId, projectName, projectWorks, allWorks, onWorkAssigned, onWorkUnassigned }) => {
  const { data, loading } = useQuery(GET_PROJECT_CATEGORIES, {
    variables: { projectId },
    fetchPolicy: 'network-only'
  });

  const [createCategory] = useMutation(CREATE_PROJECT_CATEGORY);
  const [updateCategory] = useMutation(UPDATE_PROJECT_CATEGORY);
  const [deleteCategory] = useMutation(DELETE_PROJECT_CATEGORY);
  const [moveCategory] = useMutation(MOVE_PROJECT_CATEGORY);
  const [moveWork] = useMutation(MOVE_WORK_IN_PROJECT_CATEGORY);

  const [workSearch, setWorkSearch] = useState('');
  const [activeTab, setActiveTab] = useState('unassigned');
  
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalData, setAddModalData] = useState({ parentId: null, isRoot: true });
  const [newCatName, setNewCatName] = useState('');

  const categories = (data && data.getProjectCategories) ? data.getProjectCategories : [];
  const { roots, rootWorks, unassignedWorks } = useMemo(() => buildTree(categories, projectWorks || [], allWorks || []), [categories, projectWorks, allWorks]);

  const filteredUnassignedWorks = useMemo(() => {
    if (!workSearch.trim()) return unassignedWorks;
    const searchLower = workSearch.toLowerCase();
    return unassignedWorks.filter(w => 
      w.name.toLowerCase().includes(searchLower) || 
      (w.code && w.code.toLowerCase().includes(searchLower))
    );
  }, [unassignedWorks, workSearch]);

  const filteredAssignedWorks = useMemo(() => {
    if (!workSearch.trim()) return projectWorks || [];
    const searchLower = workSearch.toLowerCase();
    return (projectWorks || []).filter(w => 
      w.name.toLowerCase().includes(searchLower) || 
      (w.code && w.code.toLowerCase().includes(searchLower))
    );
  }, [projectWorks, workSearch]);

  const handleAddRoot = () => {
    setNewCatName('');
    setAddModalData({ parentId: null, isRoot: true });
    setAddModalOpen(true);
  };

  const handleAddSub = (parentId) => {
    setNewCatName('');
    setAddModalData({ parentId, isRoot: false });
    setAddModalOpen(true);
  };

  const handleSaveNewCategory = async () => {
    if (!newCatName.trim()) return;

    let response;
    if (addModalData.isRoot) {
      response = await createCategory({
        variables: { data: { projectId: parseInt(projectId, 10), name: newCatName } },
        refetchQueries: [{ query: GET_PROJECT_CATEGORIES, variables: { projectId } }]
      });
    } else {
      response = await createCategory({
        variables: { data: { projectId: parseInt(projectId, 10), parentId: parseInt(addModalData.parentId, 10), name: newCatName } },
        refetchQueries: [{ query: GET_PROJECT_CATEGORIES, variables: { projectId } }]
      });
    }

    setAddModalOpen(false);

    const newId = response && response.data && response.data.createProjectCategory && response.data.createProjectCategory.id;
    if (newId) {
      setTimeout(() => {
        const el = document.getElementById(`cat-node-${newId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 500);
    }
  };

  const handleEdit = async (id, name) => {
    if (!name.trim()) return;
    await updateCategory({
      variables: { data: { id: parseInt(id, 10), name } },
      refetchQueries: [{ query: GET_PROJECT_CATEGORIES, variables: { projectId } }]
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete category? All subcategories will be deleted, and works will be moved to unassigned.')) return;
    
    // Find category to extract all works inside it
    const findNodeAndExtractWorks = (nodes, targetId) => {
      let ids = [];
      const extractWorksRecursive = (node) => {
        ids = [...ids, ...node.works.map(w => w.id)];
        node.children.forEach(extractWorksRecursive);
      };

      for (const node of nodes) {
        if (node.id === targetId) {
          extractWorksRecursive(node);
          return ids;
        }
        const childIds = findNodeAndExtractWorks(node.children, targetId);
        if (childIds) return childIds;
      }
      return null;
    };

    const worksToUnassign = findNodeAndExtractWorks(roots, id);

    await deleteCategory({
      variables: { id: parseInt(id, 10) },
      refetchQueries: [
        { query: GET_PROJECT_CATEGORIES, variables: { projectId } },
        { query: GET_PROJECT, variables: { projectId } }
      ]
    });

    if (worksToUnassign && worksToUnassign.length > 0 && onWorkUnassigned) {
      onWorkUnassigned(worksToUnassign);
    }
  };

  const handleDeleteWork = (workId) => {
    if (onWorkUnassigned) {
      onWorkUnassigned(workId);
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId, type } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'CATEGORY') {
      const id = parseInt(draggableId.replace('cat-', ''), 10);
      const newParentId = destination.droppableId === 'cats-root' 
        ? null 
        : parseInt(destination.droppableId.replace('cats-', ''), 10);
      
      await moveCategory({
        variables: {
          data: { id, newParentId, newSortOrder: destination.index }
        },
        refetchQueries: [{ query: GET_PROJECT_CATEGORIES, variables: { projectId } }]
      });
    }

    if (type === 'WORK') {
      const id = parseInt(draggableId.replace('assigned-work-', '').replace('work-', ''), 10);
      const isMovedToUnassigned = destination.droppableId === 'works-unassigned';
      const newCategoryId = (isMovedToUnassigned || destination.droppableId === 'works-root')
        ? null 
        : parseInt(destination.droppableId.replace('works-', ''), 10);

      await moveWork({
        variables: {
          workId: id,
          projectId,
          newCategoryId,
          newSortOrder: destination.index
        },
        refetchQueries: [{ query: GET_PROJECT, variables: { projectId } }]
      });

      if (isMovedToUnassigned && onWorkUnassigned) {
        onWorkUnassigned(id);
      } else if (!isMovedToUnassigned && onWorkAssigned) {
        onWorkAssigned(id);
      }
    }
  };

  return (
    <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
      <style>{`
        .ui.modal, .ui.page.modals, .ui.dimmer {
          transform: none !important;
          perspective: none !important;
          will-change: auto !important;
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h4>Work Breakdown Structure</h4>
      </div>

      {loading ? (
        <Loader active inline="centered" size="small" />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            
            {/* Left Column: Categories Tree */}
            <div style={{ flex: 2, border: '1px solid #ddd', padding: '10px', borderRadius: '5px', backgroundColor: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h5 style={{ margin: 0 }}>{projectName || 'Project Structure'}</h5>
                <Button type="button" size="mini" basic icon="plus" title="Add Root Category" onClick={handleAddRoot} />
              </div>
              
              <Droppable droppableId="cats-root" type="CATEGORY">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ minHeight: '50px', backgroundColor: snapshot.isDraggingOver ? '#f0f9ff' : 'transparent', marginBottom: '10px' }}
                  >
                    {roots.map((cat, index) => (
                      <CategoryNode 
                        key={`cat-${cat.id}`} 
                        category={cat} 
                        level={0} 
                        index={index}
                        onAdd={handleAddSub}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onDeleteWork={handleDeleteWork}
                      />
                    ))}
                    {provided.placeholder}
                    {roots.length === 0 && <p style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center' }}>No categories yet.</p>}
                  </div>
                )}
              </Droppable>

              <Droppable droppableId="works-root" type="WORK">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ minHeight: '50px', backgroundColor: snapshot.isDraggingOver ? '#f0f9ff' : 'transparent', borderTop: '1px dashed #ccc', paddingTop: '10px' }}
                  >
                    {rootWorks.map((work, index) => (
                      <WorkItem key={`work-${work.id}`} work={work} index={index} onDelete={handleDeleteWork} />
                    ))}
                    {provided.placeholder}
                    {rootWorks.length === 0 && <p style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center' }}>No works at project root.</p>}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Right Column: Unassigned / Assigned Works */}
            <div style={{ 
              flex: 1, 
              border: '1px solid #ddd', 
              padding: '10px', 
              borderRadius: '5px', 
              backgroundColor: '#f5f5f5', 
              display: 'flex', 
              flexDirection: 'column',
              position: 'sticky',
              top: '20px',
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <Menu tabular size="small" style={{ marginBottom: '10px' }}>
                  <Menu.Item
                    name="Unassigned"
                    active={activeTab === 'unassigned'}
                    onClick={() => setActiveTab('unassigned')}
                  />
                  <Menu.Item
                    name="Assigned"
                    active={activeTab === 'assigned'}
                    onClick={() => setActiveTab('assigned')}
                  />
                </Menu>
                
                <Input
                  icon="search"
                  size="mini"
                  placeholder="Search works..."
                  value={workSearch}
                  onChange={(e) => setWorkSearch(e.target.value)}
                  fluid
                />
              </div>

              {activeTab === 'unassigned' && (
                <Droppable droppableId="works-unassigned" type="WORK">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{ flexGrow: 1, minHeight: '100px', backgroundColor: snapshot.isDraggingOver ? '#f0f9ff' : 'transparent' }}
                    >
                      {filteredUnassignedWorks.map((work, index) => (
                        <WorkItem key={`work-${work.id}`} work={work} index={index} />
                      ))}
                      {provided.placeholder}
                      {filteredUnassignedWorks.length === 0 && <p style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>No unassigned works found.</p>}
                    </div>
                  )}
                </Droppable>
              )}

              {activeTab === 'assigned' && (
                <Droppable droppableId="works-assigned" type="WORK" isDropDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{ flexGrow: 1, minHeight: '100px' }}
                    >
                      {filteredAssignedWorks.map((work, index) => (
                        <WorkItem key={`assigned-work-${work.id}`} idPrefix="assigned-work-" work={work} index={index} />
                      ))}
                      {provided.placeholder}
                      {filteredAssignedWorks.length === 0 && <p style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>No assigned works found.</p>}
                    </div>
                  )}
                </Droppable>
              )}
            </div>

          </div>
        </DragDropContext>
      )}

      {/* Add Category Modal */}
      <Modal
        size="mini"
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        closeIcon
      >
        <Modal.Header>{addModalData.isRoot ? 'Add Root Category' : 'Add Subcategory'}</Modal.Header>
        <Modal.Content>
          <Form size="small" onSubmit={(e) => { e.preventDefault(); handleSaveNewCategory(); }}>
            <Form.Input
              fluid
              autoFocus
              label="Category Name"
              placeholder={addModalData.isRoot ? 'Enter root category name' : 'Enter subcategory name'}
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button type="button" onClick={() => setAddModalOpen(false)}>Cancel</Button>
          <Button type="button" color="teal" onClick={handleSaveNewCategory}>Save</Button>
        </Modal.Actions>
      </Modal>

    </div>
  );
};
